import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTheme, DEFAULT_FONT_SETTINGS } from '../../contexts/ThemeContext';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const ModalContainer = styled.div`
  background-color: #1a1a1a;
  border-radius: 12px;
  width: 420px;
  max-width: 95vw;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  color: #fff;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #333;
  position: sticky;
  top: 0;
  background-color: #1a1a1a;
  z-index: 1;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #888;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 4px;
  line-height: 1;

  &:hover {
    color: #fff;
  }
`;

const ModalBody = styled.div`
  padding: 20px;
`;

const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  margin-top: ${props => props.first ? '0' : '24px'};
  padding-bottom: 8px;
  border-bottom: 2px solid #444;
`;

const GroupTitle = styled.h4`
  margin: 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: #fff;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const GroupIcon = styled.span`
  font-size: 1rem;
`;

const CategorySection = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #333;
`;

const CategoryTitle = styled.h4`
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: #ffc107;
`;

const CategoryDescription = styled.span`
  font-size: 0.75rem;
  color: #888;
`;

const SettingsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const SettingItem = styled.div`
  &.full-width {
    grid-column: 1 / -1;
  }
`;

const SettingLabel = styled.label`
  display: block;
  font-size: 0.8rem;
  color: #aaa;
  margin-bottom: 4px;
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 10px;
  border-radius: 6px;
  border: 1px solid #444;
  background-color: #2a2a2a;
  color: #fff;
  font-size: 0.85rem;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #ffc107;
  }

  option {
    background-color: #2a2a2a;
  }
`;

const SliderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Slider = styled.input`
  flex: 1;
  -webkit-appearance: none;
  height: 4px;
  border-radius: 2px;
  background: #444;
  outline: none;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #ffc107;
    cursor: pointer;
    transition: transform 0.1s;
  }

  &::-webkit-slider-thumb:hover {
    transform: scale(1.1);
  }
`;

const SliderValue = styled.span`
  min-width: 45px;
  text-align: right;
  font-size: 0.8rem;
  color: #888;
  font-family: monospace;
`;

const PreviewSection = styled.div`
  margin-top: 20px;
  padding: 16px;
  background-color: #fff;
  border-radius: 8px;
  color: #333;
`;

const PreviewLabel = styled.div`
  font-size: 0.75rem;
  color: #888;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const PreviewHeading = styled.h2`
  margin: 0 0 8px 0;
  font-family: ${props => props.fontFamily}, sans-serif;
  font-size: calc(1.5rem * ${props => props.fontSize});
  font-weight: ${props => props.fontWeight};
  letter-spacing: ${props => props.letterSpacing}em;
  line-height: ${props => props.lineHeight};
  color: #1a1a1a;
`;

const PreviewBody = styled.p`
  margin: 0;
  font-family: ${props => props.fontFamily}, sans-serif;
  font-size: calc(1rem * ${props => props.fontSize});
  font-weight: ${props => props.fontWeight};
  letter-spacing: ${props => props.letterSpacing}em;
  line-height: ${props => props.lineHeight};
  color: #333;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-top: 1px solid #333;
  background-color: #1a1a1a;
`;

const ResetButton = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  border: 1px solid #555;
  background: transparent;
  color: #aaa;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #333;
    color: #fff;
  }
`;

const SaveButton = styled.button`
  padding: 8px 20px;
  border-radius: 6px;
  border: none;
  background: #ffc107;
  color: #1a1a1a;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #ffca28;
  }
`;

const FontSettingsModal = ({ isOpen, onClose }) => {
  const {
    fontSettings,
    setFontSettings,
    resetFontSettings,
    fontCategories,
    availableFonts,
  } = useTheme();

  const [localSettings, setLocalSettings] = useState(fontSettings);

  // Sync local settings when modal opens
  useEffect(() => {
    if (isOpen) {
      setLocalSettings(fontSettings);
    }
  }, [isOpen, fontSettings]);

  const handleChange = (category, property, value) => {
    setLocalSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [property]: value,
      },
    }));
  };

  const handleSave = () => {
    // Apply each category's settings
    Object.keys(localSettings).forEach(category => {
      setFontSettings(category, localSettings[category]);
    });
    onClose();
  };

  const handleReset = () => {
    resetFontSettings();
    setLocalSettings(DEFAULT_FONT_SETTINGS);
  };

  if (!isOpen) return null;

  const weightOptions = [
    { value: '300', label: 'Light (300)' },
    { value: '400', label: 'Regular (400)' },
    { value: '500', label: 'Medium (500)' },
    { value: '600', label: 'Semi Bold (600)' },
    { value: '700', label: 'Bold (700)' },
    { value: '800', label: 'Extra Bold (800)' },
  ];

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            <span>🔤</span> 폰트 설정
          </ModalTitle>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>

        <ModalBody>
          {/* Global Settings Group */}
          <GroupHeader first>
            <GroupIcon>🌐</GroupIcon>
            <GroupTitle>전역 설정</GroupTitle>
          </GroupHeader>
          {Object.entries(fontCategories)
            .filter(([, category]) => category.group === 'global')
            .map(([categoryKey, category]) => (
              <CategorySection key={categoryKey}>
                <CategoryHeader>
                  <CategoryTitle>{category.labelKo}</CategoryTitle>
                  <CategoryDescription>{category.description}</CategoryDescription>
                </CategoryHeader>

                <SettingsGrid>
                  <SettingItem className="full-width">
                    <SettingLabel>폰트</SettingLabel>
                    <Select
                      value={localSettings[categoryKey]?.fontFamily || 'Noto Sans KR'}
                      onChange={e => handleChange(categoryKey, 'fontFamily', e.target.value)}
                    >
                      <optgroup label="한글 폰트">
                        {availableFonts.filter(f => f.korean).map(font => (
                          <option key={font.value} value={font.value}>
                            {font.name}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="영문 폰트">
                        {availableFonts.filter(f => !f.korean).map(font => (
                          <option key={font.value} value={font.value}>
                            {font.name}
                          </option>
                        ))}
                      </optgroup>
                    </Select>
                  </SettingItem>

                  <SettingItem>
                    <SettingLabel>굵기</SettingLabel>
                    <Select
                      value={localSettings[categoryKey]?.fontWeight || '400'}
                      onChange={e => handleChange(categoryKey, 'fontWeight', e.target.value)}
                    >
                      {weightOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Select>
                  </SettingItem>

                  <SettingItem>
                    <SettingLabel>크기 배율</SettingLabel>
                    <SliderContainer>
                      <Slider
                        type="range"
                        min="0.8"
                        max="1.4"
                        step="0.05"
                        value={localSettings[categoryKey]?.fontSize || '1'}
                        onChange={e => handleChange(categoryKey, 'fontSize', e.target.value)}
                      />
                      <SliderValue>
                        {Math.round((localSettings[categoryKey]?.fontSize || 1) * 100)}%
                      </SliderValue>
                    </SliderContainer>
                  </SettingItem>

                  <SettingItem>
                    <SettingLabel>자간 (Letter Spacing)</SettingLabel>
                    <SliderContainer>
                      <Slider
                        type="range"
                        min="-0.05"
                        max="0.2"
                        step="0.01"
                        value={localSettings[categoryKey]?.letterSpacing || '0'}
                        onChange={e => handleChange(categoryKey, 'letterSpacing', e.target.value)}
                      />
                      <SliderValue>
                        {parseFloat(localSettings[categoryKey]?.letterSpacing || 0).toFixed(2)}em
                      </SliderValue>
                    </SliderContainer>
                  </SettingItem>

                  <SettingItem>
                    <SettingLabel>줄 간격 (Line Height)</SettingLabel>
                    <SliderContainer>
                      <Slider
                        type="range"
                        min="1"
                        max="2.5"
                        step="0.1"
                        value={localSettings[categoryKey]?.lineHeight || '1.5'}
                        onChange={e => handleChange(categoryKey, 'lineHeight', e.target.value)}
                      />
                      <SliderValue>
                        {parseFloat(localSettings[categoryKey]?.lineHeight || 1.5).toFixed(1)}
                      </SliderValue>
                    </SliderContainer>
                  </SettingItem>
                </SettingsGrid>
              </CategorySection>
            ))}

          {/* Hero Section Settings Group */}
          <GroupHeader>
            <GroupIcon>🎯</GroupIcon>
            <GroupTitle>Hero 섹션</GroupTitle>
          </GroupHeader>
          {Object.entries(fontCategories)
            .filter(([, category]) => category.group === 'hero')
            .map(([categoryKey, category]) => (
              <CategorySection key={categoryKey}>
                <CategoryHeader>
                  <CategoryTitle>{category.labelKo}</CategoryTitle>
                  <CategoryDescription>{category.description}</CategoryDescription>
                </CategoryHeader>

                <SettingsGrid>
                  <SettingItem className="full-width">
                    <SettingLabel>폰트</SettingLabel>
                    <Select
                      value={localSettings[categoryKey]?.fontFamily || 'Noto Sans KR'}
                      onChange={e => handleChange(categoryKey, 'fontFamily', e.target.value)}
                    >
                      <optgroup label="한글 폰트">
                        {availableFonts.filter(f => f.korean).map(font => (
                          <option key={font.value} value={font.value}>
                            {font.name}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="영문 폰트">
                        {availableFonts.filter(f => !f.korean).map(font => (
                          <option key={font.value} value={font.value}>
                            {font.name}
                          </option>
                        ))}
                      </optgroup>
                    </Select>
                  </SettingItem>

                  <SettingItem>
                    <SettingLabel>굵기</SettingLabel>
                    <Select
                      value={localSettings[categoryKey]?.fontWeight || '400'}
                      onChange={e => handleChange(categoryKey, 'fontWeight', e.target.value)}
                    >
                      {weightOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Select>
                  </SettingItem>

                  <SettingItem>
                    <SettingLabel>크기 배율</SettingLabel>
                    <SliderContainer>
                      <Slider
                        type="range"
                        min="0.8"
                        max="1.4"
                        step="0.05"
                        value={localSettings[categoryKey]?.fontSize || '1'}
                        onChange={e => handleChange(categoryKey, 'fontSize', e.target.value)}
                      />
                      <SliderValue>
                        {Math.round((localSettings[categoryKey]?.fontSize || 1) * 100)}%
                      </SliderValue>
                    </SliderContainer>
                  </SettingItem>

                  <SettingItem>
                    <SettingLabel>자간 (Letter Spacing)</SettingLabel>
                    <SliderContainer>
                      <Slider
                        type="range"
                        min="-0.05"
                        max="0.2"
                        step="0.01"
                        value={localSettings[categoryKey]?.letterSpacing || '0'}
                        onChange={e => handleChange(categoryKey, 'letterSpacing', e.target.value)}
                      />
                      <SliderValue>
                        {parseFloat(localSettings[categoryKey]?.letterSpacing || 0).toFixed(2)}em
                      </SliderValue>
                    </SliderContainer>
                  </SettingItem>

                  <SettingItem>
                    <SettingLabel>줄 간격 (Line Height)</SettingLabel>
                    <SliderContainer>
                      <Slider
                        type="range"
                        min="1"
                        max="2.5"
                        step="0.1"
                        value={localSettings[categoryKey]?.lineHeight || '1.5'}
                        onChange={e => handleChange(categoryKey, 'lineHeight', e.target.value)}
                      />
                      <SliderValue>
                        {parseFloat(localSettings[categoryKey]?.lineHeight || 1.5).toFixed(1)}
                      </SliderValue>
                    </SliderContainer>
                  </SettingItem>
                </SettingsGrid>
              </CategorySection>
            ))}

          {/* Preview Section */}
          <PreviewSection>
            <PreviewLabel>전역 미리보기</PreviewLabel>
            <PreviewHeading
              fontFamily={localSettings.headings?.fontFamily}
              fontSize={localSettings.headings?.fontSize}
              fontWeight={localSettings.headings?.fontWeight}
              letterSpacing={localSettings.headings?.letterSpacing}
              lineHeight={localSettings.headings?.lineHeight}
            >
              제목 텍스트 Preview
            </PreviewHeading>
            <PreviewBody
              fontFamily={localSettings.body?.fontFamily}
              fontSize={localSettings.body?.fontSize}
              fontWeight={localSettings.body?.fontWeight}
              letterSpacing={localSettings.body?.letterSpacing}
              lineHeight={localSettings.body?.lineHeight}
            >
              본문 텍스트입니다. 이 텍스트는 폰트 설정의 미리보기를 제공합니다.
            </PreviewBody>
          </PreviewSection>

          {/* Hero Preview Section */}
          <PreviewSection style={{ backgroundColor: '#2d3748', marginTop: '16px' }}>
            <PreviewLabel style={{ color: '#a0aec0' }}>Hero 섹션 미리보기</PreviewLabel>
            <PreviewHeading
              fontFamily={localSettings.heroEnglish?.fontFamily}
              fontSize={localSettings.heroEnglish?.fontSize}
              fontWeight={localSettings.heroEnglish?.fontWeight}
              letterSpacing={localSettings.heroEnglish?.letterSpacing}
              lineHeight={localSettings.heroEnglish?.lineHeight}
              style={{ color: '#fff' }}
            >
              Your Trusted Partner in Real Estate
            </PreviewHeading>
            <PreviewBody
              fontFamily={localSettings.heroKorean?.fontFamily}
              fontSize={localSettings.heroKorean?.fontSize}
              fontWeight={localSettings.heroKorean?.fontWeight}
              letterSpacing={localSettings.heroKorean?.letterSpacing}
              lineHeight={localSettings.heroKorean?.lineHeight}
              style={{ color: 'rgba(255,255,255,0.9)' }}
            >
              믿고 맡기는 부동산 전문가
            </PreviewBody>
          </PreviewSection>
        </ModalBody>

        <ModalFooter>
          <ResetButton onClick={handleReset}>
            초기화
          </ResetButton>
          <SaveButton onClick={handleSave}>
            적용하기
          </SaveButton>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
};

FontSettingsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default FontSettingsModal;
